import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Music, MapPin, Search, Disc3 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { usePageMeta } from '@/hooks/usePageMeta';
import api from '@/services/api';

// Public DJ directory — APPROVED DJs only, served by GET /api/djs (the backend
// projection is the authority on what is public; this page only renders it).
function DJCard({ dj }) {
  return (
    <Link to={`/djs/${dj.slug}`} className="group block">
      <Card className="overflow-hidden border-border/50 hover-lift h-full">
        <div className="aspect-square w-full overflow-hidden bg-muted">
          {dj.profile_picture ? (
            <img
              src={dj.profile_picture}
              alt={dj.dj_name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <Disc3 className="w-12 h-12 opacity-40" />
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <h3 className="font-display font-bold text-lg truncate group-hover:text-primary transition-colors">{dj.dj_name}</h3>
          {dj.city && (
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="w-3.5 h-3.5" /> {dj.city}
            </p>
          )}
          {dj.genres?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {dj.genres.slice(0, 4).map((g) => (
                <Badge key={g} variant="secondary" className="text-xs capitalize">{g}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export default function PublicDJs() {
  usePageMeta('DJs', 'Discover SoundWolves DJs — browse approved artists, their cities and genres, and open a DJ profile to follow or book.');
  const [djs, setDjs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async (q) => {
    setLoading(true); setError(null);
    try {
      const qs = q ? `?search=${encodeURIComponent(q)}` : '';
      setDjs(await api.get(`/djs${qs}`));
    } catch (e) {
      setError(e.message || 'Failed to load DJs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(query); }, [query, load]);

  return (
    <div className="max-w-7xl mx-auto px-4 pt-20 pb-24 md:pb-8">
      <div className="mb-6">
        <h1 className="text-3xl font-display font-bold flex items-center gap-2"><Music className="w-7 h-7 text-primary" /> DJs</h1>
        <p className="text-muted-foreground mt-1">Browse our approved DJ roster.</p>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }} className="relative max-w-md mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, city, or genre" className="pl-9" />
      </form>

      {loading && <p className="text-center py-16 opacity-60">Loading DJs…</p>}
      {error && !loading && <p className="text-center py-16 text-destructive">{error}</p>}
      {!loading && !error && djs.length === 0 && (
        <p className="text-center py-16 opacity-60">{query ? 'No DJs match your search.' : 'No DJs are listed yet.'}</p>
      )}

      {!loading && !error && djs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {djs.map((dj) => <DJCard key={dj.slug} dj={dj} />)}
        </div>
      )}
    </div>
  );
}
